<script setup>
/**
 * ImmersiveQuickAdd
 * Quantity control for the immersive catalog. Reuses the shared
 * ItemDialogQtyStepper for all stepper logic/validation and delegates every
 * mutation to the cart store (which already enforces the onlyBox / boxSize
 * rules), so no business logic is duplicated here. Dark/light theming is applied
 * by remapping the design tokens the stepper consumes onto the `--imm-*` vars.
 */
import { computed, ref, watch } from 'vue'
import { useCartStore } from '../../../../app/store/cart.js'
import { hapticTap } from '../../../../core/utils/haptics.js'
import ItemDialogQtyStepper from '../item-dialog/actions/ItemDialogQtyStepper.vue'

const props = defineProps({
  item: { type: Object, default: null },
  isOnlyBox: { type: Boolean, default: false },
  boxSize: { type: Number, default: 1 },
  stepSize: { type: Number, default: 1 },
  hasValidPrice: { type: Boolean, default: false },
  isOutOfStock: { type: Boolean, default: false },
})

const cartStore = useCartStore()

const makat = computed(() => props.item?.makat || null)
const cartQty = computed(() => (makat.value ? cartStore.getQty(makat.value) : 0))
const isInCart = computed(() => cartQty.value > 0)

const disabled = computed(() => !props.hasValidPrice || props.isOutOfStock)

// Inline "box multiples only" hint, shown once the user interacts invalidly or
// while the item is in the cart (so the rule stays visible).
const showBoxMessage = ref(false)
watch(makat, () => {
  showBoxMessage.value = false
})

// Qty-change "juice": haptic tick + a pop pulse on the number. Two alternating
// (identical) animation classes so rapid consecutive taps restart the pulse
// without a forced reflow. Fires for any source (stepper, typing, ladder tap).
const bumpState = ref(0)
watch(cartQty, (nv, ov) => {
  if (nv === ov) return
  hapticTap()
  if (nv > 0) bumpState.value = bumpState.value === 1 ? 2 : 1
})

// Boxes equivalence caption (e.g. "3 מארזים") when the item is box-packaged.
const boxesText = computed(() => {
  if (props.boxSize <= 1 || cartQty.value <= 0) return null
  const boxes = cartQty.value / props.boxSize
  if (Number.isInteger(boxes)) return `${boxes} מארזים`
  return `${boxes.toFixed(1)} מארזים`
})

function handleQtyUpdate(newQty) {
  if (!makat.value || disabled.value) return
  if (props.isOnlyBox && cartQty.value === 0) showBoxMessage.value = true

  // setQty handles first-add (falls back to addItem internally), box-multiple
  // validation, and removal at 0.
  const result = cartStore.setQty(makat.value, newQty)
  if (!result.ok) {
    if (
      result.message?.startsWith('QTY_NOT_BOX_MULTIPLE') ||
      result.message === 'INVALID_BOXSIZE'
    ) {
      showBoxMessage.value = true
    }
  }
}

function handleRemove() {
  if (!makat.value) return
  cartStore.removeItem(makat.value)
}

function handleInvalid() {
  if (props.isOnlyBox) showBoxMessage.value = true
}
</script>

<template>
  <div class="imm-quickadd">
    <!-- Disabled states -->
    <div v-if="isOutOfStock" class="imm-qa-note imm-qa-note-error">חסר זמנית במלאי</div>
    <div v-else-if="!hasValidPrice" class="imm-qa-note imm-qa-note-error">אין מחיר זמין לפריט</div>

    <template v-else>
      <span class="imm-qa-label">כמות להזמנה</span>

      <!-- The remove button is always laid out (revealed only once in cart) so
           adding it never shifts the +/- controls. The stepper keeps its slot. -->
      <div
        class="imm-qa-row"
        :class="{
          'is-in-cart': isInCart,
          'qty-bump-a': bumpState === 1,
          'qty-bump-b': bumpState === 2,
        }"
      >
        <button
          type="button"
          class="imm-qa-trash"
          :class="{ 'is-reserved': !isInCart }"
          :tabindex="isInCart ? 0 : -1"
          :aria-hidden="!isInCart"
          aria-label="הסר מהעגלה"
          @click="handleRemove"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
            <line x1="10" y1="11" x2="10" y2="17" />
            <line x1="14" y1="11" x2="14" y2="17" />
          </svg>
        </button>

        <ItemDialogQtyStepper
          :qty="cartQty"
          :step="stepSize"
          :only-box="isOnlyBox"
          :box-size="boxSize"
          :disabled="disabled"
          @update:qty="handleQtyUpdate"
          @remove="handleRemove"
          @invalid="handleInvalid"
        />
      </div>

      <div class="imm-qa-meta">
        <span v-if="isInCart" class="imm-qa-chip">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          בעגלה
        </span>
        <!-- Mobile-only remove pill (the row trash is hidden <768 so the big
             stepper stays perfectly centered). -->
        <button
          v-if="isInCart"
          type="button"
          class="imm-qa-trash-mini"
          aria-label="הסר מהעגלה"
          @click="handleRemove"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
          </svg>
          הסר
        </button>
        <span v-if="boxesText" class="imm-qa-boxes">{{ boxesText }}</span>
      </div>

      <!-- onlyBox notice: reserved for box-only items (laid out, hidden until
           relevant) so revealing it never nudges the stepper above it. -->
      <div
        v-if="isOnlyBox"
        class="imm-qa-boxmsg"
        :class="{ 'is-reserved': !(showBoxMessage || isInCart) }"
      >
        ניתן להזמין רק במכפלות של {{ boxSize }}
      </div>
    </template>
  </div>
</template>

<style scoped>
.imm-quickadd {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  /* Remap the tokens the reused stepper consumes onto the immersive theme so the
     shared component themes itself (no :deep needed for colors). */
  --bg-secondary: var(--imm-field-bg, rgba(255, 255, 255, 0.07));
  --bg-tertiary: var(--imm-step-active-bg, rgba(59, 130, 246, 0.22));
  --border-color: var(--imm-field-border, rgba(255, 255, 255, 0.18));
  --text-primary: var(--imm-text, #ffffff);
  --color-primary: var(--imm-accent, #3b82f6);
}

.imm-qa-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--imm-text-muted, rgba(255, 255, 255, 0.65));
}

.imm-qa-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

/* Enlarge the reused stepper for comfortable touch targets in the overlay.
   Sizes are token-driven so they scale up per breakpoint (mobile/tablet/PC).
   The controls are the star of this screen: rounder shell, springy press
   feedback, and a soft accent glow once the item is in the cart. */
.imm-qa-row :deep(.qty-stepper) {
  border-radius: 18px;
  border-width: 1.5px;
  transition:
    box-shadow 0.2s ease,
    border-color 0.2s ease;
}

.imm-qa-row.is-in-cart :deep(.qty-stepper) {
  border-color: rgba(96, 165, 250, 0.45);
  box-shadow: 0 0 22px rgba(59, 130, 246, 0.2);
}

.imm-qa-row :deep(.stepper-btn) {
  width: var(--imm-step-size, 56px);
  height: var(--imm-step-size, 56px);
}

.imm-qa-row :deep(.stepper-btn svg) {
  width: 26px;
  height: 26px;
}

.imm-qa-row :deep(.stepper-btn:active:not(:disabled)) {
  transform: scale(0.88);
}

.imm-qa-row :deep(.qty-input) {
  width: var(--imm-qty-w, 64px);
  height: var(--imm-step-size, 56px);
  font-size: var(--imm-step-font, 1.5rem);
  font-variant-numeric: tabular-nums;
}

/* Qty pop pulse — two identical alternating animations (restart trick). */
.imm-qa-row.qty-bump-a :deep(.qty-input) {
  animation: imm-qty-pop-a 0.28s cubic-bezier(0.16, 1, 0.3, 1);
}

.imm-qa-row.qty-bump-b :deep(.qty-input) {
  animation: imm-qty-pop-b 0.28s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes imm-qty-pop-a {
  0% {
    transform: scale(1);
  }
  40% {
    transform: scale(1.22);
    color: var(--imm-accent, #60a5fa);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes imm-qty-pop-b {
  0% {
    transform: scale(1);
  }
  40% {
    transform: scale(1.22);
    color: var(--imm-accent, #60a5fa);
  }
  100% {
    transform: scale(1);
  }
}

.imm-qa-trash {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--imm-trash-size, 48px);
  height: var(--imm-trash-size, 48px);
  flex-shrink: 0;
  border-radius: 14px;
  border: 1.5px solid var(--imm-field-border, rgba(255, 255, 255, 0.18));
  background: var(--imm-field-bg, rgba(255, 255, 255, 0.07));
  color: var(--imm-text-muted, rgba(255, 255, 255, 0.7));
  cursor: pointer;
  transition: all 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.imm-qa-trash:active {
  transform: scale(0.92);
  background: rgba(239, 68, 68, 0.2);
  border-color: var(--color-error, #ef4444);
  color: var(--color-error, #ef4444);
}

/* Kept in the layout (slot reserved) but invisible until the item is in cart. */
.imm-qa-trash.is-reserved {
  visibility: hidden;
  pointer-events: none;
}

.imm-qa-trash svg {
  width: 22px;
  height: 22px;
}

.imm-qa-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  /* Reserve the full height of the tallest occupant (the "בעגלה" chip ≈ 23px)
     so first-add can't nudge the panel below by a few pixels. */
  min-height: 24px;
}

/* Mobile remove pill — hidden on tablet/PC where the row trash exists. */
.imm-qa-trash-mini {
  display: none;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid rgba(239, 68, 68, 0.45);
  background: rgba(239, 68, 68, 0.12);
  color: var(--imm-error, #f87171);
  font-size: 0.74rem;
  font-weight: 700;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.imm-qa-trash-mini:active {
  transform: scale(0.94);
  background: rgba(239, 68, 68, 0.22);
}

.imm-qa-trash-mini svg {
  width: 14px;
  height: 14px;
}

/* Mobile: the standalone trash leaves the stepper row (its reserved slot was
   pushing the +/- off-center) and the compact remove pill above takes over. */
@media (max-width: 767px) {
  .imm-qa-trash {
    display: none;
  }

  .imm-qa-trash-mini {
    display: inline-flex;
  }

  .imm-qa-meta {
    min-height: 30px;
  }
}

.imm-qa-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--imm-success, #34d399);
  background: rgba(16, 185, 129, 0.16);
  border: 1px solid rgba(16, 185, 129, 0.4);
  /* Pops in on first add (the v-if mounts it fresh). */
  animation: imm-chip-pop 0.32s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes imm-chip-pop {
  0% {
    opacity: 0;
    transform: scale(0.6);
  }
  60% {
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.imm-qa-chip svg {
  width: 13px;
  height: 13px;
}

.imm-qa-boxes {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--imm-text-faint, rgba(255, 255, 255, 0.55));
}

.imm-qa-boxmsg {
  font-size: 0.74rem;
  font-weight: 600;
  text-align: center;
  color: var(--imm-warning, #fbbf24);
}

/* Reserved for box-only items: occupies its line but hidden until relevant. */
.imm-qa-boxmsg.is-reserved {
  visibility: hidden;
}

.imm-qa-note {
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  padding: 12px;
}

.imm-qa-note-error {
  color: var(--imm-error, #f87171);
}

@media (prefers-reduced-motion: reduce) {
  .imm-qa-trash,
  .imm-qa-row :deep(.qty-stepper) {
    transition: none;
  }

  .imm-qa-chip,
  .imm-qa-row.qty-bump-a :deep(.qty-input),
  .imm-qa-row.qty-bump-b :deep(.qty-input) {
    animation: none;
  }
}
</style>
